import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import LandingPage from "./LandingPage"; // ⬅️ In src/, not in components!
import LoginPage from "./LoginPage";
import SignUpPage from "./SignUpPage";
import MainPage from "./MainPage";
import BookingHistoryPage from "./BookingHistoryPage";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import RefundCancellationPolicy from "./components/RefundCancellationPolicy";

// ✅ Policy CSS import
import "./components/policy.css";

// 🔥 Firebase Auth
import { auth } from "./utils/firebase";
import { onAuthStateChanged } from "firebase/auth";

// 🔒 AuthGate protects logged-in pages
function AuthGate({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthed(!!user);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (!authed) return <Navigate to="/" state={{ from: location }} replace />;
  return <>{children}</>;
}

function App() {
  // Called after successful login/signup
  const handleLoginSuccess = () => {
    // You can trigger a reload, redirect, show a message, etc. as you want here
    //window.location.reload(); // (optional: if you want to force user state refresh)
  };

  return (
    <BrowserRouter>
      <Routes>
        {/* PUBLIC LANDING PAGE at / */}
        <Route path="/" element={<LandingPage onLoginSuccess={handleLoginSuccess} />} />

        {/* Other public routes (if you still want direct login/signup route, otherwise can hide) */}
        <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/signup" element={<SignUpPage onLoginSuccess={handleLoginSuccess} />} />

        {/* PROTECTED ROUTES */}
        <Route
          path="/main"
          element={
            <AuthGate>
              <MainPage />
            </AuthGate>
          }
        />
        <Route
          path="/booking-history"
          element={
            <AuthGate>
              <BookingHistoryPage onClose={() => {}} />
            </AuthGate>
          }
        />

        {/* POLICY ROUTES */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicy />} />

        {/* REDIRECT EVERYTHING ELSE TO LANDING PAGE */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
